import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { apiAuth } from "@/lib/api";

export const TagsPanel = ({ entityType, entityId, initialTags = [] }) => {
  const apiBase = `/admin/${entityType}s/${entityId}/tags`;
  const [tags, setTags] = useState(initialTags);
  const [value, setValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setTags(initialTags); }, [initialTags?.length]);  // eslint-disable-line

  useEffect(() => {
    if (!value.trim()) { setSuggestions([]); return; }
    const id = setTimeout(async () => {
      try {
        const { data } = await apiAuth.get(`/admin/tags/suggest?q=${encodeURIComponent(value)}&category=${entityType}`);
        setSuggestions(data.items || []);
      } catch (_e) { setSuggestions([]); }
    }, 200);
    return () => clearTimeout(id);
  }, [value, entityType]);

  const add = async (name) => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const { data } = await apiAuth.post(apiBase, { name: name.trim() });
      setTags((t) => [...new Set([...t, data.tag.slug])]);
      setValue(""); setSuggestions([]);
    } catch (_e) { /* ignore */ }
    finally { setSaving(false); }
  };

  const remove = async (slug) => {
    try {
      await apiAuth.delete(`${apiBase}/${encodeURIComponent(slug)}`);
      setTags((t) => t.filter((x) => x !== slug));
    } catch (_e) { /* ignore */ }
  };

  return (
    <div className="flex flex-col gap-3" data-testid={`tags-${entityType}`}>
      <div className="flex flex-wrap gap-2">
        {tags.length === 0 && <p className="text-sm text-warmGrey">Noch keine Tags.</p>}
        {tags.map((slug) => (
          <span key={slug} className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs bg-roseMilk text-cocoa" data-testid={`tag-${slug}`}>
            {slug}
            <button type="button" onClick={() => remove(slug)} aria-label="Remove tag" className="hover:text-coral">
              <X size={11} />
            </button>
          </span>
        ))}
      </div>
      <div className="relative max-w-md">
        <form onSubmit={(e) => { e.preventDefault(); add(value); }} className="flex gap-2">
          <input
            className="wit-input flex-1"
            placeholder="Tag hinzufügen …"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            data-testid="tag-input"
          />
          <button type="submit" disabled={saving || !value.trim()} className="wit-btn-secondary text-xs disabled:opacity-60" data-testid="tag-add">
            Hinzufügen
          </button>
        </form>
        {suggestions.length > 0 && (
          <ul className="absolute top-full left-0 right-0 mt-1 bg-white border border-sandBeige rounded-2xl shadow-soft z-10 max-h-48 overflow-y-auto">
            {suggestions.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => add(s.name)}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-ivory"
                >
                  {s.name} <span className="text-warmGrey text-xs">({s.usage_count})</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default TagsPanel;
